// src/components/ChangePrimaryEmail.tsx

import { useState } from "react";
import { updatePrimaryEmail, verifyEmailUpdate } from "../auth/cognitoAuth";

interface ChangePrimaryEmailProps {
  currentEmail: string;
  onSuccess: (newEmail: string) => void;
  onCancel: () => void;
}

const ChangePrimaryEmail = ({ currentEmail, onSuccess, onCancel }: ChangePrimaryEmailProps) => {
  const [newEmail, setNewEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [step, setStep] = useState<"input" | "verify">("input");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!newEmail.trim()) {
      setError("Please enter a new email address.");
      return;
    }

    if (!isValidEmail(newEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setError("New email must be different from current email.");
      return;
    }

    setLoading(true);

    try {
      const result = await updatePrimaryEmail(newEmail);
      if (result.success) {
        setSuccess("Verification code sent to new email!");
        setStep("verify");
      } else {
        setError(result.error || "Failed to send verification code.");
      }
    } catch (err) {
      setError("Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!verificationCode.trim()) {
      setError("Please enter the verification code.");
      return;
    }

    setLoading(true);

    try {
      const result = await verifyEmailUpdate(verificationCode);
      if (result.success) {
        setSuccess("Email updated successfully!");
        // Give user a moment to see success message
        setTimeout(() => {
          onSuccess(newEmail);
        }, 1500);
      } else {
        setError(result.error || "Invalid verification code.");
      }
    } catch (err) {
      setError("Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl border-4 border-gray-950 max-w-md w-full p-6">
        <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1a1a', marginBottom: 16 }}>
          Change Primary Email
        </h2>

        <div className="mb-4 p-3 bg-gray-100 rounded">
          <p className="text-sm text-gray-600">Current Email:</p>
          <p className="font-medium text-gray-800">{currentEmail}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        {step === "input" ? (
          <form onSubmit={handleSendCode}>
            <div className="mb-4">
              <label
                htmlFor="newEmail"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                New Email Address
              </label>
              <input
                id="newEmail"
                type="email"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setError("");
                }}
                placeholder="Enter new email"
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
              <p className="text-xs" style={{ color: '#555' }}>
                <strong>Note:</strong> A verification code will be sent to your new email.
                Your current email will remain accessible as a contact point that can be deleted.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold text-white ${
                  loading
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-brand hover:bg-branddark"
                }`}
              >
                {loading ? "Sending..." : "Send Code"}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerify}>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Enter the verification code sent to:
              </p>
              <p className="font-medium text-gray-800 mb-4">{newEmail}</p>

              <input
                type="text"
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value);
                  setError("");
                }}
                placeholder="Enter verification code"
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setStep("input");
                  setVerificationCode("");
                  setError("");
                }}
                className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold text-white ${
                  loading
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-brand hover:bg-branddark"
                }`}
              >
                {loading ? "Verifying..." : "Verify"}
              </button>
            </div>

            <p className="text-xs text-center text-gray-500 mt-3">
              Didn't receive the code? Check your spam folder or{" "}
              <button
                type="button"
                onClick={() => {
                  setStep("input");
                  setVerificationCode("");
                  setError("");
                }}
                style={{ color: '#c0645a' }} className="underline"
              >
                try again
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePrimaryEmail;